import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Tooltip,
  Slider,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Avatar,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  MusicNote as MusicNoteIcon,
  Piano as PianoIcon,
  AudioFile as AudioFileIcon,
  Lyrics as LyricsIcon,
  Album as AlbumIcon,
  PlayArrow as PlayArrowIcon,
  LibraryMusic as LibraryMusicIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { MusicPromptService } from '../services/musicPromptService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import {
  MusicPromptSession,
  MusicPromptIteration,
  MusicAgentType,
  GenerateMusicIterationRequest
} from '../models/musicPrompt';

const agentIcons: Record<MusicAgentType, React.ReactElement> = {
  Composer: <MusicNoteIcon />,
  Arranger: <PianoIcon />,
  Producer: <AudioFileIcon />,
  Lyricist: <LyricsIcon />,
  SoundDesigner: <AlbumIcon />
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`music-tabpanel-${index}`}
      aria-labelledby={`music-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 } }}>{children}</Box>}
    </div>
  );
}

const MusicPromptIterator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch persisted sessions from backend
  const {
    sessions: backendSessions,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'MusicPromptSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true,
  });

  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<MusicPromptSession[]>([]);
  const [currentSession, setCurrentSession] = useState<MusicPromptSession | null>(null);
  // New session creation fields
  const [initialPrompt, setInitialPrompt] = useState('');
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionGenre, setSessionGenre] = useState('');
  const [sessionPurpose, setSessionPurpose] = useState('');
  const [targetDuration, setTargetDuration] = useState<number | undefined>(undefined);
  const [maxResponseTokens, setMaxResponseTokens] = useState(2000);
  const [sessionTemperature, setSessionTemperature] = useState(0.7);
  
  // Iteration fields
  const [selectedAgentType, setSelectedAgentType] = useState<MusicAgentType>('Composer');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genre, setGenre] = useState('');
  const [subGenres, setSubGenres] = useState<string[]>([]);
  const [eraOrPeriod, setEraOrPeriod] = useState('');
  const [genreSpecificTechniques, setGenreSpecificTechniques] = useState('');
  const [mood, setMood] = useState('');
  const [tempo, setTempo] = useState(120);
  const [instruments, setInstruments] = useState('');
  const [maxTokens, setMaxTokens] = useState(2000);

  // Load sessions on mount
  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    const localSessions = MusicPromptService.loadSessions();

    if (backendSessions && backendSessions.length > 0) {
      const converted: MusicPromptSession[] = backendSessions.map(bs => ({
        id: bs.session.id,
        title: bs.session.metadata?.title || 'Untitled Music Session',
        initialPrompt: bs.session.metadata?.initialPrompt || '',
        currentPrompt: bs.session.metadata?.currentPrompt || bs.session.metadata?.initialPrompt || '',
        sessionType: bs.session.sessionType as 'Standard' | 'Niche' | undefined,
        iterations: bs.assets
          .filter(a => a.subType === 'Iteration')
          .map(a => JSON.parse(a.textContent || '{}'))
          .sort((a, b) => a.iterationNumber - b.iterationNumber) || [],
        createdAt: new Date(bs.session.createdAt),
        lastUpdatedAt: bs.session.updatedAt ? new Date(bs.session.updatedAt) : new Date(bs.session.createdAt),
      }));

      const sessionMap = new Map<string, MusicPromptSession>();
      localSessions.forEach(s => sessionMap.set(s.id, s));
      converted.forEach(s => sessionMap.set(s.id, s));

      const merged = Array.from(sessionMap.values());
      setSessions(merged);
      if (merged.length > 0 && !currentSession) {
        setCurrentSession(merged[0]);
      }
    } else {
      setSessions(localSessions);
      if (localSessions.length > 0 && !currentSession) {
        setCurrentSession(localSessions[0]);
      }
    }
  }, [backendSessions]); // Removed refetchBackendData() call and currentSession dependency

  const validateSessionForm = (): string | null => {
    if (!initialPrompt.trim()) {
      return 'Music concept description is required';
    }
    if (initialPrompt.trim().length < 10) {
      return 'Music concept description must be at least 10 characters';
    }
    if (targetDuration && (targetDuration < 5 || targetDuration > 3600)) {
      return 'Target duration must be between 5 seconds and 1 hour';
    }
    if (maxResponseTokens < 500 || maxResponseTokens > 4000) {
      return 'Max response tokens must be between 500 and 4000';
    }
    return null;
  };

  const createNewSession = async () => {
    // Validate required fields
    const validationError = validateSessionForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await MusicPromptService.startSession(initialPrompt, 'Standard', {
        title: sessionTitle,
        genre: sessionGenre,
        purpose: sessionPurpose,
        targetDuration: targetDuration,
        maxResponseTokens: maxResponseTokens,
        temperature: sessionTemperature
      });
      
      // Since sessionId can be null, we need to check
      if (!response.sessionId) {
        throw new Error('Failed to get session ID from response');
      }
      
      const newSession: MusicPromptSession = {
        id: response.sessionId,
        title: sessionTitle.trim() || response.title || initialPrompt.substring(0, 50) + (initialPrompt.length > 50 ? '...' : ''),
        initialPrompt: initialPrompt,
        currentPrompt: response.concept || initialPrompt,
        sessionType: 'Standard',
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
        iterations: [{
          iterationNumber: 1,
          prompt: response.concept || initialPrompt,
          promptVersion: 'v1',
          agentType: 'Composer',
          timestamp: new Date(),
          isUserAction: false,
          genre: sessionGenre || undefined,
          tempo: targetDuration || undefined
        }]
      };

      MusicPromptService.saveSession(newSession);
      setCurrentSession(newSession);
      setTabValue(2); // Switch to Current Session tab
      // Clear form
      setInitialPrompt('');
      setSessionTitle('');
      setSessionGenre('');
      setSessionPurpose('');
      setTargetDuration(undefined);
      refetchBackendData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const generateIteration = async () => {
    if (!currentSession) return;

    // Validate required fields
    if (!genre.trim()) {
      setError('Please specify a genre for iteration');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: GenerateMusicIterationRequest = {
        agentType: selectedAgentType,
        specificGenre: genre.trim(),
        subGenres: subGenres,
        eraOrPeriod: eraOrPeriod.trim() || '',
        genreSpecificTechniques: genreSpecificTechniques.trim() || '',
        additionalInstructions: additionalInstructions.trim() || '',
        maxTokens: maxTokens,
        temperature: temperature
      };

      const response = await MusicPromptService.generateIteration(currentSession.id, request);
      
      const newIterationNumber = currentSession.iterations.length + 1;
      const newIteration: MusicPromptIteration = {
        iterationNumber: newIterationNumber,
        prompt: response.generatedPrompt || currentSession.currentPrompt,
        promptVersion: `v${newIterationNumber}`,
        agentType: selectedAgentType,
        timestamp: new Date(),
        userInstruction: additionalInstructions || undefined,
        isUserAction: false,
        genre: genre || undefined,
        mood: mood || undefined,
        tempo: tempo || undefined,
        instruments: instruments ? instruments.split(',').map(i => i.trim()) : undefined
      };

      const updatedSession: MusicPromptSession = {
        ...currentSession,
        currentPrompt: newIteration.prompt,
        lastUpdatedAt: new Date(),
        iterations: [...currentSession.iterations, newIteration]
      };

      MusicPromptService.updateSession(updatedSession);
      setCurrentSession(updatedSession);
      setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
      
      // Clear form fields
      setAdditionalInstructions('');
      setGenreSpecificTechniques('');
      setEraOrPeriod('');
      setSubGenres([]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate iteration');
    } finally {
      setLoading(false);
    }
  };

  const selectSession = (session: MusicPromptSession) => {
    setCurrentSession(session);
    setError(null);
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    MusicPromptService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    refetchBackendData();
  };

  const copyPrompt = async (prompt: string) => {
    try {
      await MusicPromptService.copyToClipboard(prompt);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const exportSession = () => {
    if (currentSession) {
      MusicPromptService.exportSession(currentSession);
    }
  };

  const importSession = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const importedSession = await MusicPromptService.importSession(file);
      MusicPromptService.saveSession(importedSession);
      setSessions([importedSession, ...sessions]);
      setCurrentSession(importedSession);
    } catch (err: any) {
      setError(err.message || 'Failed to import session');
    }
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '2.125rem' }
          }}
        >
          <LibraryMusicIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Music & Sound Effect Studio
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Create and refine music and sound effect prompts with AI-powered agents
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3, width: '100%', overflow: 'hidden' }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        >
          <Tab label="Sessions" />
          <Tab label="Create New" />
          {currentSession && <Tab label="Current Session" />}
        </Tabs>
      </Paper>
      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              No sessions found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTabValue(1)}
              size="large"
              fullWidth={isMobile}
              sx={{ 
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                mt: { xs: 1, sm: 0 }
              }}
            >
              Create Your First Session
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sessions.map((session) => (
                <Card 
                  key={session.id} 
                  variant="outlined" 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => { selectSession(session); setTabValue(2); }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                        >
                          {session.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Created: {new Date(session.createdAt).toLocaleDateString()} • {session.iterations.length} iterations
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={(e) => deleteSession(session.id, e)}
                        color="error"
                        sx={{ minHeight: 44, minWidth: 44 }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Create New Session */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
          >
            Create New Music Session
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Music Concept *"
              value={initialPrompt}
              onChange={(e) => setInitialPrompt(e.target.value)}
              placeholder="Describe the music or sound effect you want to create..."
              required
              error={!initialPrompt.trim() || initialPrompt.trim().length < 10}
              helperText={
                !initialPrompt.trim() 
                  ? "Music concept is required" 
                  : initialPrompt.trim().length < 10 
                    ? "Must be at least 10 characters" 
                    : `${initialPrompt.length} characters`
              }
            />
            
            <TextField
              fullWidth
              label="Session Title"
              value={sessionTitle}
              onChange={(e) => setSessionTitle(e.target.value)}
              placeholder="Give your session a memorable name"
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Primary Genre</InputLabel>
                  <Select
                    value={sessionGenre}
                    onChange={(e) => setSessionGenre(e.target.value)}
                    label="Primary Genre"
                  >
                    <MenuItem value="">None specified</MenuItem>
                    <MenuItem value="electronic">Electronic</MenuItem>
                    <MenuItem value="pop">Pop</MenuItem>
                    <MenuItem value="rock">Rock</MenuItem>
                    <MenuItem value="jazz">Jazz</MenuItem>
                    <MenuItem value="classical">Classical</MenuItem>
                    <MenuItem value="hip-hop">Hip-Hop</MenuItem>
                    <MenuItem value="country">Country</MenuItem>
                    <MenuItem value="folk">Folk</MenuItem>
                    <MenuItem value="ambient">Ambient</MenuItem>
                    <MenuItem value="cinematic">Cinematic</MenuItem>
                    <MenuItem value="world">World Music</MenuItem>
                    <MenuItem value="experimental">Experimental</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Purpose</InputLabel>
                  <Select
                    value={sessionPurpose}
                    onChange={(e) => setSessionPurpose(e.target.value)}
                    label="Purpose"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="production">Music Production</MenuItem>
                    <MenuItem value="sound-design">Sound Design</MenuItem>
                    <MenuItem value="composition">Composition</MenuItem>
                    <MenuItem value="arrangement">Arrangement</MenuItem>
                    <MenuItem value="mixing">Mixing</MenuItem>
                    <MenuItem value="mastering">Mastering</MenuItem>
                    <MenuItem value="soundtrack">Soundtrack</MenuItem>
                    <MenuItem value="jingle">Jingle/Commercial</MenuItem>
                    <MenuItem value="podcast">Podcast/Audio</MenuItem>
                    <MenuItem value="game">Game Audio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Target Duration (seconds)"
                  type="number"
                  value={targetDuration || ''}
                  onChange={(e) => setTargetDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                  InputProps={{
                    inputProps: { min: 5, max: 3600 }
                  }}
                  placeholder="Optional duration in seconds"
                  error={targetDuration !== undefined && (targetDuration < 5 || targetDuration > 3600)}
                  helperText={
                    targetDuration !== undefined && (targetDuration < 5 || targetDuration > 3600)
                      ? "Duration must be between 5 seconds and 1 hour (3600s)"
                      : "Leave empty for no specific duration"
                  }
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Response Tokens"
                  type="number"
                  value={maxResponseTokens}
                  onChange={(e) => setMaxResponseTokens(parseInt(e.target.value) || 2000)}
                  InputProps={{
                    inputProps: { min: 500, max: 4000 }
                  }}
                  error={maxResponseTokens < 500 || maxResponseTokens > 4000}
                  helperText={
                    maxResponseTokens < 500 || maxResponseTokens > 4000
                      ? "Must be between 500 and 4000 tokens"
                      : "Higher values = more detailed responses"
                  }
                />
              </Grid>
            </Grid>
            
            <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
              <Typography 
                variant="body2" 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                AI Creativity Level: {sessionTemperature}
              </Typography>
              <Slider
                value={sessionTemperature}
                onChange={(_, value) => setSessionTemperature(value as number)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: 'Conservative' },
                  { value: 0.5, label: 'Balanced' },
                  { value: 1, label: 'Creative' }
                ]}
                valueLabelDisplay="auto"
                sx={{ 
                  mx: { xs: 1, sm: 2 },
                  '& .MuiSlider-markLabel': {
                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                  }
                }}
              />
            </Box>
            
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={createNewSession}
              disabled={loading || validateSessionForm() !== null}
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
              sx={{ 
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {loading ? 'Creating Session...' : 'Create Music Session'}
            </Button>
            
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        </Paper>
      </TabPanel>

      {currentSession && (
        <TabPanel value={tabValue} index={2}>
          {/* Current Session */}
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {/* Session Header */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 0 },
                mb: 2 
              }}>
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {currentSession.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Export Session">
                    <IconButton 
                      onClick={exportSession}
                      sx={{ minHeight: 44, minWidth: 44 }}
                    >
                      <DownloadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Import Session">
                    <IconButton 
                      component="label"
                      sx={{ minHeight: 44, minWidth: 44 }}
                    >
                      <UploadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      <input
                        type="file"
                        hidden
                        accept=".json"
                        onChange={importSession}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Current Prompt Display */}
              <Box>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  Current Prompt
                </Typography>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      gap: { xs: 1, sm: 0 },
                      mb: 1 
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Version {currentSession.iterations.length}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => copyPrompt(currentSession.currentPrompt)}
                        sx={{ minHeight: 36, minWidth: 36 }}
                      >
                        <CopyIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      </IconButton>
                    </Box>
                    <Box sx={{ 
                      '& p': { 
                        margin: 0,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }, 
                      '& h1, & h2, & h3, & h4, & h5, & h6': { 
                        margin: '8px 0',
                        fontSize: { xs: '1rem', sm: '1.125rem' }
                      } 
                    }}>
                      <ReactMarkdown>{currentSession.currentPrompt}</ReactMarkdown>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Controls */}
              <Box>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  Generate Iteration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Agent Type</InputLabel>
                      <Select
                        value={selectedAgentType}
                        onChange={(e) => setSelectedAgentType(e.target.value as MusicAgentType)}
                        label="Agent Type"
                      >
                        {(['Composer', 'Arranger', 'Producer', 'Lyricist', 'SoundDesigner'] as MusicAgentType[]).map((agent) => (
                          <MenuItem key={agent} value={agent}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {agentIcons[agent]}
                              {agent}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Specific Genre *"
                      value={genre}
                      onChange={(e) => setGenre(e.target.value)}
                      placeholder="e.g., Electronic, Jazz, Classical"
                      required
                      error={!genre.trim()}
                      helperText={!genre.trim() ? "Genre is required for iteration" : ""}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={['Progressive', 'Fusion', 'Contemporary', 'Traditional', 'Experimental', 'Minimal', 'Orchestral', 'Acoustic', 'Electronic', 'Hybrid']}
                      value={subGenres}
                      onChange={(_, newValue) => setSubGenres(newValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub-Genres"
                          placeholder="Add sub-genres or styles"
                          helperText="Optional: Add specific sub-genres or musical styles"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Era or Period"
                      value={eraOrPeriod}
                      onChange={(e) => setEraOrPeriod(e.target.value)}
                      placeholder="e.g., 1980s, Modern, Classical Period, Renaissance"
                      helperText="Musical era, decade, or historical period"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Max Tokens"
                      type="number"
                      value={maxTokens}
                      onChange={(e) => setMaxTokens(parseInt(e.target.value) || 2000)}
                      InputProps={{
                        inputProps: { min: 100, max: 4000 }
                      }}
                      helperText="Response length (100-4000 tokens)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Genre-Specific Techniques"
                      value={genreSpecificTechniques}
                      onChange={(e) => setGenreSpecificTechniques(e.target.value)}
                      placeholder="e.g., Jazz: swing rhythm, blue notes; Electronic: sidechain compression, filter sweeps"
                      helperText="Techniques, methods, or characteristics specific to this genre"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Additional Instructions"
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      placeholder="Any other specific guidance or requirements..."
                      helperText="General instructions or special requirements"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          generateIteration();
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                      <Typography 
                        variant="body2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Temperature: {temperature}
                      </Typography>
                      <Slider
                        value={temperature}
                        onChange={(_, value) => setTemperature(value as number)}
                        min={0}
                        max={1}
                        step={0.1}
                        marks={[
                          { value: 0, label: 'Precise' },
                          { value: 0.5, label: 'Balanced' },
                          { value: 1, label: 'Creative' }
                        ]}
                        valueLabelDisplay="auto"
                        sx={{ 
                          mx: { xs: 1, sm: 2 },
                          '& .MuiSlider-markLabel': {
                            fontSize: { xs: '0.65rem', sm: '0.75rem' }
                          }
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      onClick={generateIteration}
                      disabled={loading || !genre.trim()}
                      startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                      sx={{ 
                        minHeight: 44,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {loading ? 'Generating...' : 'Generate Iteration'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Error Display */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Iteration History */}
              {currentSession.iterations.length > 0 && (
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                  >
                    Iteration History
                  </Typography>
                  <Stack spacing={2}>
                    {currentSession.iterations.map((iteration, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: { xs: 1, sm: 0 },
                            mb: 1 
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: 1 
                            }}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 } }}>
                                {agentIcons[iteration.agentType]}
                              </Avatar>
                              <Typography 
                                variant="subtitle2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                              >
                                {iteration.agentType} - Iteration {iteration.iterationNumber}
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap',
                                gap: 0.5 
                              }}>
                                {iteration.genre && <Chip label={iteration.genre} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
                                {iteration.mood && <Chip label={iteration.mood} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
                                {iteration.tempo && <Chip label={`${iteration.tempo} BPM`} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
                              </Box>
                            </Box>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: 1 
                            }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              >
                                {iteration.timestamp.toLocaleString()}
                              </Typography>
                              <Tooltip title="Copy to clipboard">
                                <IconButton 
                                  size="small" 
                                  onClick={() => copyPrompt(iteration.prompt)}
                                  sx={{ minHeight: 36, minWidth: 36 }}
                                >
                                  <CopyIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          {iteration.userInstruction && (
                            <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                <strong>User instruction:</strong> {iteration.userInstruction}
                              </Typography>
                            </Alert>
                          )}
                          {iteration.instruments && iteration.instruments.length > 0 && (
                            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {iteration.instruments.map((instrument, i) => (
                                <Chip key={i} label={instrument} size="small" sx={{ mr: 0, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                              ))}
                            </Box>
                          )}
                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'primary.50' }}>
                            <Box sx={{ 
                              '& p': { 
                                margin: '4px 0', 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }, 
                              '& h1, & h2, & h3, & h4, & h5, & h6': { 
                                margin: '6px 0', 
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                fontWeight: 600 
                              },
                              '& ul, & ol': { 
                                margin: '4px 0', 
                                paddingLeft: { xs: '16px', sm: '20px' }
                              },
                              '& li': { 
                                margin: '2px 0',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }
                            }}>
                              <ReactMarkdown>{iteration.prompt}</ReactMarkdown>
                            </Box>
                          </Paper>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Paper>
        </TabPanel>
      )}
    </Container>
  );
};

export default MusicPromptIterator;